import type { ApiKey } from '@/features/api-keys/types';
import { SCOPE_LABELS, SCOPE_PERMISSIONS, isExpired, isExpiringSoon, getKeyStatus } from '@/features/api-keys/types';

/**
 * Utility functions for API key management
 */

// Mask an API key prefix (e.g., "asab_ag_abc1" → "asab_ag_abc1****")
export const maskApiKey = (keyPrefix: string): string => {
  if (!keyPrefix) return '****************';
  return `${keyPrefix}****`;
};

// Re-export type helpers
export { isExpired as isApiKeyExpired, isExpiringSoon as isApiKeyExpiringSoon };

// Get the display status of an API key
export const getApiKeyStatus = (apiKey: ApiKey): 'active' | 'expired' | 'expiring_soon' => {
  return getKeyStatus(apiKey);
};

// Get status color for display
export const getApiKeyStatusColor = (status: string): string => {
  switch (status) {
    case 'active':
      return 'text-green-600 bg-green-100';
    case 'expired':
      return 'text-red-600 bg-red-100';
    case 'expiring_soon':
      return 'text-yellow-600 bg-yellow-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
};

// Format API key created date
export const formatApiKeyDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format time ago
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
};

// Get days until expiry
export const getDaysUntilExpiry = (apiKey: ApiKey): number | null => {
  if (!apiKey.expires_at) return null;
  const expiryDate = new Date(apiKey.expires_at);
  const now = new Date();
  return Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
};

// Format expiry information
export const formatExpiryInfo = (apiKey: ApiKey): string => {
  if (!apiKey.expires_at) return 'Never expires';

  const daysUntilExpiry = getDaysUntilExpiry(apiKey);
  if (daysUntilExpiry === null) return 'Never expires';

  if (daysUntilExpiry < 0) return 'Expired';
  if (daysUntilExpiry === 0) return 'Expires today';
  if (daysUntilExpiry === 1) return 'Expires tomorrow';
  if (daysUntilExpiry < 7) return `Expires in ${daysUntilExpiry} days`;
  if (daysUntilExpiry < 30) {
    const weeks = Math.floor(daysUntilExpiry / 7);
    return `Expires in ${weeks} week${weeks > 1 ? 's' : ''}`;
  }
  if (daysUntilExpiry < 365) {
    const months = Math.floor(daysUntilExpiry / 30);
    return `Expires in ${months} month${months > 1 ? 's' : ''}`;
  }

  const years = Math.floor(daysUntilExpiry / 365);
  return `Expires in ${years} year${years > 1 ? 's' : ''}`;
};

// Copy text to clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const result = document.execCommand('copy');
      document.body.removeChild(textArea);
      return result;
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// Format key type for display
export const formatKeyType = (apiKey: ApiKey): string => {
  if (apiKey.key_type === 'scope' && apiKey.scope) {
    return SCOPE_LABELS[apiKey.scope] || apiKey.scope;
  }
  return 'Fine-grained';
};

// Format permissions for display
export const formatPermissions = (apiKey: ApiKey): string => {
  if (apiKey.key_type === 'scope' && apiKey.scope) {
    return SCOPE_PERMISSIONS[apiKey.scope] || '';
  }
  if (apiKey.permissions && apiKey.permissions.length > 0) {
    return apiKey.permissions.join(', ');
  }
  return '';
};

// Sort API keys by creation date (newest first)
export const sortApiKeysByDate = (apiKeys: ApiKey[]): ApiKey[] => {
  return [...apiKeys].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
};

// Sort API keys by name
export const sortApiKeysByName = (apiKeys: ApiKey[]): ApiKey[] => {
  return [...apiKeys].sort((a, b) => a.name.localeCompare(b.name));
};

// Filter API keys by status
export const filterApiKeysByStatus = (apiKeys: ApiKey[], status: string): ApiKey[] => {
  return apiKeys.filter(key => getApiKeyStatus(key) === status);
};

// Search API keys by name or prefix
export const searchApiKeys = (apiKeys: ApiKey[], searchTerm: string): ApiKey[] => {
  if (!searchTerm.trim()) return apiKeys;

  const lowercaseSearch = searchTerm.toLowerCase();
  return apiKeys.filter(
    key =>
      key.name.toLowerCase().includes(lowercaseSearch) ||
      key.key_prefix.toLowerCase().includes(lowercaseSearch)
  );
};

// Get API key statistics
export const getApiKeyStats = (apiKeys: ApiKey[]) => {
  const total = apiKeys.length;
  const active = apiKeys.filter(key => getApiKeyStatus(key) === 'active').length;
  const expired = apiKeys.filter(key => isExpired(key)).length;
  const expiring = apiKeys.filter(key => isExpiringSoon(key)).length;

  return {
    total,
    active,
    expired,
    expiring,
  };
};
